/* eslint-disable max-lines */
/* eslint-disable no-nested-ternary */
import tokens from '@kluein/design-tokens';
import { Box, Text } from '@kluein/klue-ui';
import { marked } from 'marked';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { AddAnswerToDigestModal } from 'components/common/ask-klue-add-to-digest-modal';
import { CardScheduleModal } from 'components/common/card-schedule-modal';
import { UserAvatar } from 'components/common/user-avatar';
import { Scroller } from 'components/layout/scroller';
import { useAuth } from 'contexts/auth';
import { useAskKlueLog } from 'hooks/dashboard/use-ask-klue-log';
import { useCardViewer } from 'hooks/use-card-viewer';
import { decodeCommonEntities } from 'klue-html/formulas';
import { AnalyticsAction } from 'lib/analytics';
import { AnswerChat } from 'modules/current-compete/libs/ask-klue-answer-mode-chat/components/answer-chat';
import { ResearchChat } from 'modules/current-compete/libs/ask-klue-research-mode-chat/components/research-chat';
import { HATCH_TOOL_REGISTRY } from 'modules/hatch/libs/chat/utils/tool-calling-utils';
import { ChatStatusHelpers } from 'modules/shared/libs/chat/constants';
import {
  getEffectiveCitationTitle,
  getPublicCitationUrl,
  resolveFootnoteCitationsToUrls,
} from 'modules/shared/libs/chat/utils/sources.utils';
import { buildToolFriendlyNamesMap } from 'modules/shared/libs/chat/utils/tool-calling-utils';
import { useAskKlue } from 'pages/dashboard/hooks';
import ExportModal from 'pages/export/components/ExportModal';
import { useAskKlueWizardId } from 'store/views';
import TEST_IDS from 'test-ids';

import modalStyles from './AskKlueModalBody.module.css';

import AskKlueCreateCardModal from '../ask-klue-create-card-modal';
import { AskKlueLoading } from '../ask-klue-loading';
import { AskKlueModalWizard } from '../ask-klue-modal-wizard';

import type { UserType } from 'api/api.types';
import type { UseAnswerChatReturnType } from 'modules/current-compete/libs/ask-klue-answer-mode-chat/hooks/useAnswerChat';
import type { UseResearchChatReturnType } from 'modules/current-compete/libs/ask-klue-research-mode-chat/hooks/useResearchChat';
import type { Source } from 'modules/shared/api/sources.api.types';
import type { CitationSource } from 'modules/shared/libs/chat/types';
import type { AskKlueReturnType } from 'store/api/ask-klue/askKlue.types';
import type { RootState } from 'store/store.types';

const AvatarMemo = memo(({ userId }: { userId: number }) => (
  <UserAvatar userId={userId} size={24} radius="xl" showBorder={false} />
));

function ModalBodyError() {
  const { t } = useTranslation(['Dashboard']);
  return (
    <Box height="100vh" pad={{ vertical: 'xxlarge' }}>
      <Box>
        <Text
          fontWeight="medium"
          lineHeight="sparse"
          color={tokens.color.priority.critical[800]}
        >
          {t('Dashboard:askKlue.error.networkErrorTitle')}
        </Text>
        <Text
          fontWeight="medium"
          lineHeight="sparse"
          color={tokens.color.neutral.black.main}
        >
          {t('Dashboard:askKlue.error.networkGenericError')}
        </Text>
      </Box>
    </Box>
  );
}

function ModalBody({ question }: { question: string }) {
  const boxRef = useRef<HTMLDivElement>(null);
  const user = useSelector<RootState, UserType | null>(
    (state) => state.auth.user,
  );

  useEffect(() => {
    if (boxRef.current) {
      const scrollToBottom = () => {
        requestAnimationFrame(() => {
          boxRef.current?.scrollIntoView({
            behavior: 'smooth',
          });
        });
      };

      scrollToBottom();
    }
  }, []);

  return (
    <Box flex={{ grow: 1 }} ref={boxRef}>
      <Box flex={{ shrink: 0 }} margin={{ bottom: 'large' }}>
        <Box direction="row" align="center" gap="small">
          <AvatarMemo userId={user?.id as number} />
          <Text
            size={tokens.fontSize.bodyFont.large}
            color={tokens.color.primary.dark}
            fontFamily={tokens.fontFamily.Proxima}
            fontWeight="semibold"
            lineHeight={tokens.lineHeight.dense}
          >
            {user?.firstName}
          </Text>
        </Box>
        <Box pad={{ left: 'xxlarge' }}>
          <Text
            size={tokens.fontSize.bodyFont.medium}
            color={tokens.color.neutral.darkgrey.main}
            lineHeight={tokens.lineHeight.dense}
            fontFamily={tokens.fontFamily.Inter}
            className={modalStyles.questionText}
            data-test-id={TEST_IDS.askKlue.text}
          >
            {decodeCommonEntities(question)}
          </Text>
        </Box>
      </Box>
      <Box
        pad="xlarge"
        background={tokens.color.neutral.white.main}
        round="xsmall"
        border={{ color: tokens.color.neutral.lightgrey.main }}
        flex={{ shrink: 0 }}
      >
        <AskKlueLoading />
      </Box>
    </Box>
  );
}

function AskKlueModalBody({
  pendingQuestion,
  isPage = false,
  activeChat,
  isResearchChatActive = false,
  modernSources,
  modernDisabledSources,
  onDidSubmitAskKlue,
}: {
  pendingQuestion?: string;
  isPage?: boolean;
  activeChat?: UseResearchChatReturnType | UseAnswerChatReturnType;
  isResearchChatActive?: boolean;
  modernSources?: Source[];
  modernDisabledSources?: string[];
  onDidSubmitAskKlue?: () => void;
}) {
  const { t } = useTranslation(['Dashboard']);
  const { isCurator, isChatExportEnabled } = useAuth();
  const { handleAskKlue, askKlueQueryParams } = useAskKlue();
  const logAskKlueEvent = useAskKlueLog();
  const [showCreateCard, setShowCreateCard] = useState(false);
  const [createCardAnswer, setCreateCardAnswer] =
    useState<AskKlueReturnType | null>(null);
  const [createCardQuestion, setCreateCardQuestion] = useState<string>('');

  // State for tracking linked cards in modern research mode
  const [linkedCardIds, setLinkedCardIds] = useState<Record<string, number>>(
    {},
  );
  const [pendingMessageId, setPendingMessageId] = useState<string | null>(null);
  const [pendingResearchMode, setPendingResearchMode] = useState(false);
  const [pendingDisabledSourceTypes, setPendingDisabledSourceTypes] = useState<
    string[]
  >([]);

  // State for scheduling cards in modern research mode
  const [schedulingCardId, setSchedulingCardId] = useState<number | null>(null);

  // State for export modal in modern research mode
  const [exportMessageId, setExportMessageId] = useState<string | null>(null);

  // State for the Add-to-Digest modal opened from an Ask Klue answer. Holds
  // the answer's snapshot at the moment the button was clicked — separate
  // from the Create Card path since the two flows don't share a modal.
  const [addToDigestAnswer, setAddToDigestAnswer] = useState<{
    answerId: string;
    answerHtml: string;
    title: string;
    userQuestion?: string;
  } | null>(null);

  // Card viewer hook for View Card functionality
  const { open: openCardViewer } = useCardViewer();

  // Build tool friendly names map from sources
  const toolFriendlyNames = useMemo(
    () =>
      modernSources
        ? buildToolFriendlyNamesMap(modernSources, HATCH_TOOL_REGISTRY)
        : undefined,
    [modernSources],
  );

  const wizardId = useAskKlueWizardId();

  const boxRef = useRef<HTMLDivElement>(null);
  const modernChatRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (
      boxRef.current &&
      askKlueQueryParams?.askKlueSessionId &&
      askKlueQueryParams?.askKlueCategory
    ) {
      boxRef.current.scrollTop = 0;
    }
  }, [askKlueQueryParams]);

  // Scroll to bottom when the user sends a message in the active modern chat
  // (research or answer mode).
  const modernMessages = activeChat?.messages;
  useEffect(() => {
    if (modernChatRef.current && modernMessages?.length) {
      const lastMessage = modernMessages[modernMessages.length - 1];
      // Only scroll when user sends a message (not on assistant responses)
      if (lastMessage?.role === 'user') {
        requestAnimationFrame(() => {
          modernChatRef.current?.scrollTo({
            top: modernChatRef.current.scrollHeight,
            behavior: 'smooth',
          });
        });
      }
    }
  }, [modernMessages]);

  // Handler for the active modern chat (research or answer) — transforms
  // CitationSource[] to AskKlueSourceType[] before opening the create-card modal.
  const handleCreateCardFromModernChat = useCallback(
    (
      messageId: string,
      messageText: string,
      sources: CitationSource[],
      userQuestion?: string,
    ) => {
      // Resolve [^N] footnote citations to [N](url) links before HTML conversion
      const resolvedText = resolveFootnoteCitationsToUrls(messageText, sources);
      // Convert markdown to HTML for card storage (sync mode)
      const htmlContent = marked(resolvedText, { async: false });

      // Transform CitationSource[] to AskKlueSourceType[]
      const transformedSources = sources
        .map((source) => ({
          title: getEffectiveCitationTitle(source),
          url: getPublicCitationUrl(source),
          doc_type: 'card' as const,
        }))
        .filter(
          (
            source,
          ): source is { title: string; url: string; doc_type: 'card' } =>
            !!source.url,
        );

      // Create a compatible AskKlueReturnType object
      // Use persistedId so the card schedule's askKlueAnswerId matches reloaded thread IDs
      const answer: AskKlueReturnType = {
        id: messageId,
        text: htmlContent,
        sources: transformedSources,
        rivals: [],
        thread_id: activeChat?.threadId,
      };

      // Keep the streaming ID for the in-memory linkedCardIds map (current session)
      setPendingMessageId(messageId);
      // Research-mode flag drives the create-card modal's auto-prompt
      // generation (which uses a research-mode export endpoint). Only set
      // it for actual research chats — answer-mode chats use a different
      // backend and would 404 / behave incorrectly otherwise.
      setPendingResearchMode(isResearchChatActive);
      setPendingDisabledSourceTypes(modernDisabledSources ?? []);
      setCreateCardAnswer(answer);
      setCreateCardQuestion(
        userQuestion ||
          t('Dashboard:askKlue.createCardModal.defaultResearchModeTitle'),
      );
      setShowCreateCard(true);
    },
    [isResearchChatActive, modernDisabledSources, activeChat?.threadId, t],
  );

  // Snapshots the answer and opens AddAnswerToDigestModal. Independent of
  // the Create Card flow — the digest item materializes directly from the
  // answer via /api/ask_klue_answers/add_to_digest, no card intermediary.
  const handleAddAnswerToDigest = useCallback(
    (
      messageId: string,
      messageText: string,
      sources: CitationSource[],
      userQuestion?: string,
    ) => {
      const resolvedText = resolveFootnoteCitationsToUrls(messageText, sources);
      const htmlContent = marked(resolvedText, { async: false });
      setAddToDigestAnswer({
        answerId: messageId,
        answerHtml: htmlContent,
        title: userQuestion || 'Ask Klue Answer',
        userQuestion,
      });
    },
    [],
  );

  const handleExportFromModernChat = useCallback(
    (
      messageId: string,
      _messageText: string,
      _sources: CitationSource[],
      _userQuestion?: string,
    ) => {
      setExportMessageId(messageId);
    },
    [],
  );

  const handleCloseCreateCard = useCallback(
    (createdCardId?: number) => {
      // If a card was created and we have a pending message, track the link
      if (createdCardId && pendingMessageId) {
        setLinkedCardIds((prev) => ({
          ...prev,
          [pendingMessageId]: createdCardId,
        }));
      }
      setShowCreateCard(false);
      setCreateCardAnswer(null);
      setCreateCardQuestion('');
      setPendingMessageId(null);
      setPendingResearchMode(false);
      setPendingDisabledSourceTypes([]);
      // Open the card viewer for the newly created card
      if (createdCardId) {
        openCardViewer(createdCardId);
      }
    },
    [pendingMessageId, openCardViewer],
  );

  // Handler for View Card button - opens card viewer
  const handleViewCard = useCallback(
    (cardId: number) => {
      openCardViewer(cardId);
    },
    [openCardViewer],
  );

  // Handler for Schedule Card button - opens schedule modal
  const handleScheduleCard = useCallback((cardId: number) => {
    setSchedulingCardId(cardId);
  }, []);

  const userMessageCount =
    modernMessages?.filter((m) => m.role === 'user').length ?? 0;

  const createCardModal = showCreateCard && createCardAnswer && (
    <AskKlueCreateCardModal
      answer={createCardAnswer}
      question={createCardQuestion}
      onClose={handleCloseCreateCard}
      researchMode={pendingResearchMode}
      disabledSourceTypes={pendingDisabledSourceTypes}
      userMessageCount={userMessageCount}
    />
  );

  const addToDigestModal = addToDigestAnswer && (
    <AddAnswerToDigestModal
      answerId={addToDigestAnswer.answerId}
      answerHtml={addToDigestAnswer.answerHtml}
      title={addToDigestAnswer.title}
      userQuestion={addToDigestAnswer.userQuestion}
      onClose={() => setAddToDigestAnswer(null)}
    />
  );

  if (wizardId) {
    return <AskKlueModalWizard handleAskKlue={handleAskKlue} />;
  }

  // Modern answer-thread restore failed — show the error UI
  // instead of falling through to an empty body. AskKlueContent disables
  // the input while hasLoadError is set, so without this branch the user
  // would land on a blank, unrecoverable screen.
  if (activeChat?.hasLoadError) {
    return <ModalBodyError />;
  }

  if (pendingQuestion) {
    return (
      <Box pad={{ vertical: 'xxlarge' }} overflow="auto">
        <ModalBody question={pendingQuestion} />
      </Box>
    );
  }

  // Modern chat mode (Research or Answer)
  // Show modern chat if there are messages OR if we're loading history
  const hasModernMessages = (activeChat?.messages?.length ?? 0) > 0;
  const isLoadingModernHistory = activeChat?.isLoadingHistory ?? false;
  if (activeChat && (hasModernMessages || isLoadingModernHistory)) {
    const sharedChatProps = {
      messages: activeChat.messages,
      status: activeChat.status,
      error: activeChat.error,
      regenerate: activeChat.regenerate,
      stop: activeChat.stop,
      sessionId: activeChat.threadId,
      isLoadingHistory: activeChat.isLoadingHistory,
      headerHeight: 80,
      bodyPadding: 16,
      // Card actions are curator-only.
      // Passing undefined hides each button in CardActions inside the chat components.
      onCreateCard: isCurator ? handleCreateCardFromModernChat : undefined,
      // Add to Digest is curator-only. Independent of Create Card — shows
      // regardless of whether a card is linked to the answer.
      onAddToDigest: isCurator ? handleAddAnswerToDigest : undefined,
      // Export is available to admins and curators, and to consumers when an
      // admin has switched it on in Chat Customization. Research mode was
      // already curator-gated by isResearchChatActive, but answer mode is
      // where consumers land, so the check has to be explicit here.
      onExport: isChatExportEnabled ? handleExportFromModernChat : undefined,
      linkedCardIds,
      onViewCard: isCurator ? handleViewCard : undefined,
      onScheduleCard: isCurator ? handleScheduleCard : undefined,
      toolFriendlyNames,
    };

    // Use 100vh to ensure content displays regardless of flex context
    // The parent container handles the actual scrolling constraints
    return (
      <>
        <Box
          ref={modernChatRef}
          height="100vh"
          overflow="auto"
          pad={{ top: 'xxlarge' }}
          style={{ fontSize: '16px' }}
        >
          {isResearchChatActive ? (
            <ResearchChat {...sharedChatProps} />
          ) : (
            <AnswerChat
              {...sharedChatProps}
              onFollowupClick={(followup) => {
                // Guard double-clicks and clicks while a response is still
                // streaming so we don't enqueue duplicate submissions —
                // mirrors the Hatch followup handlers.
                if (!ChatStatusHelpers.canSubmit(activeChat.status)) return;
                // Sending straight to activeChat bypasses handleAskKlue, so
                // log the ask_followup event explicitly to keep followup
                // engagement visible.
                logAskKlueEvent({
                  askKlueId: '',
                  eventType: AnalyticsAction.askFollowup,
                  text: followup.action,
                });
                activeChat.sendMessage(followup.action);
                // Notify the page so it can collapse the faded dashboard,
                // matching every other modern submit entry point.
                onDidSubmitAskKlue?.();
              }}
            />
          )}
        </Box>
        {createCardModal}
        {addToDigestModal}
        {schedulingCardId && (
          <CardScheduleModal
            cardId={schedulingCardId}
            onSave={() => {
              setSchedulingCardId(null);
            }}
            onClose={() => setSchedulingCardId(null)}
          />
        )}
        {exportMessageId && (
          <ExportModal
            opened={!!exportMessageId}
            onClose={() => setExportMessageId(null)}
            messageId={exportMessageId}
          />
        )}
      </>
    );
  }

  if (isPage) {
    return null;
  }

  return (
    <>
      <Box>
        <Scroller disableFade padScrollbar={false} ref={boxRef}>
          <Box pad={{ vertical: 'xxlarge' }} gap="xxlarge" height="100vh" />
        </Scroller>
      </Box>
      {createCardModal}
      {addToDigestModal}
    </>
  );
}

export default AskKlueModalBody;
